import * as React from 'react';
import { Pie, PieChart as RechartsPieChart, Sector } from 'recharts';

import { ChartContainer } from './ChartContainer';
import type {
  ChartConfig,
  ChartDataPoint,
  ChartPointValue,
  PieChartProps,
} from './types';
import { resolvePieChartColor } from './utils';
import { ChartTooltip } from './ChartTooltip';
import { cn } from '../../lib/utils';
import { useLocaleFormat } from '../../hooks/useLocaleFormat';

const DEFAULT_INNER_RADIUS = '60%';

type RenderSectorProps = React.ComponentProps<typeof Sector> & {
  index?: number;
  payload?: Record<string, unknown>;
};

export function PieChart<DataPoint extends ChartDataPoint = ChartDataPoint>({
  data,
  dataKey,
  nameKey,
  ariaLabel,
  className,
  width = '100%',
  height,
  aspect = 1,
  showLegend = true,
  showTooltip = true,
  legendProps,
  innerRadius = DEFAULT_INNER_RADIUS,
  paddingAngle = 2,
  config,
  labelFormatter,
  valueFormat = 'percent',
  valueFormatter,
}: PieChartProps<DataPoint>) {
  const { formatNumber } = useLocaleFormat();

  const derivedConfig = React.useMemo<ChartConfig>(() => {
    if (config) return config;

    return data.reduce<ChartConfig>((acc, entry, index) => {
      const key = String((nameKey ? entry[nameKey] : entry[dataKey]) ?? index);
      acc[key] = {
        label: (entry[nameKey ?? dataKey] ?? key) as string,
        color: resolvePieChartColor(undefined, index),
      };
      return acc;
    }, {} as ChartConfig);
  }, [config, data, dataKey, nameKey]);

  const [highlightedLegendIndex, setHighlightedLegendIndex] = React.useState<
    number | null
  >(null);
  const legendRowRefs = React.useRef<Array<HTMLTableRowElement | null>>([]);

  const handleHighlight = React.useCallback((index: number | null) => {
    setHighlightedLegendIndex(index);
  }, []);

  const handleLegendKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTableRowElement>, currentIndex: number) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const totalItems = legendRowRefs.current.length;
      if (totalItems === 0) return;

      const nextIndex = (currentIndex + direction + totalItems) % totalItems;
      legendRowRefs.current[nextIndex]?.focus();
    },
    [],
  );

  const colouredData = React.useMemo(
    () =>
      data.map((entry, index) => {
        const key = String(
          (nameKey ? entry[nameKey] : entry[dataKey]) ?? index,
        );
        return {
          ...entry,
          fill:
            (entry as { fill?: string }).fill ??
            derivedConfig[key]?.color ??
            resolvePieChartColor(undefined, index),
        };
      }),
    [data, dataKey, nameKey, derivedConfig],
  );

  const legendItems = React.useMemo(
    () =>
      data.map((entry, index) => {
        const key = String(
          (nameKey ? entry[nameKey] : entry[dataKey]) ?? index,
        );
        const label =
          derivedConfig[key]?.label ??
          (entry[nameKey ?? dataKey] as string) ??
          key;
        const color =
          derivedConfig[key]?.color || resolvePieChartColor(undefined, index);
        const value = entry[dataKey];

        return {
          key,
          label,
          color,
          value,
          index,
          entry,
        };
      }),
    [data, dataKey, nameKey, derivedConfig],
  );

  React.useEffect(() => {
    legendRowRefs.current = legendRowRefs.current.slice(0, legendItems.length);
  }, [legendItems.length]);

  const renderSector = React.useCallback(
    (props: RenderSectorProps) => {
      const sliceIndex = props.index ?? 0;
      const key = String(
        (nameKey ? props.payload?.[nameKey] : props.payload?.[dataKey]) ??
          sliceIndex,
      );
      const color =
        derivedConfig[key]?.color ||
        resolvePieChartColor(undefined, sliceIndex);
      const isActive = highlightedLegendIndex === sliceIndex;
      const hasActiveSlice = highlightedLegendIndex !== null;

      return (
        <Sector
          {...props}
          fill={color}
          stroke='var(--color-white)'
          strokeWidth={isActive ? 4 : 1}
          fillOpacity={hasActiveSlice ? (isActive ? 1 : 0.45) : 1}
        />
      );
    },
    [dataKey, derivedConfig, highlightedLegendIndex, nameKey],
  );

  const formatValue = React.useCallback(
    (value: ChartPointValue) => {
      if (valueFormatter) {
        return valueFormatter(value);
      }

      if (value == null) {
        return '';
      }

      if (typeof value !== 'number') {
        return String(value);
      }

      return valueFormat === 'percent'
        ? formatNumber(`${value}%`)
        : formatNumber(value);
    },
    [formatNumber, valueFormat, valueFormatter],
  );

  return (
    <div className='eye:flex eye:w-full eye:flex-col eye:md:flex-row eye:md:gap-xl'>
      <ChartContainer
        ariaLabel={ariaLabel}
        className={className}
        config={derivedConfig}
        width={width}
        height={height}
        aspect={aspect}
      >
        <RechartsPieChart>
          {showTooltip ? (
            <ChartTooltip
              labelFormatter={labelFormatter}
              valueFormatter={(value) => formatValue(value)}
            />
          ) : null}
          <Pie
            data={colouredData}
            dataKey={dataKey as string}
            nameKey={(nameKey ?? dataKey) as string}
            innerRadius={innerRadius}
            paddingAngle={paddingAngle}
            cornerRadius={6}
            legendType='rect'
            isAnimationActive={false}
            shape={renderSector}
          />
        </RechartsPieChart>
      </ChartContainer>
      {showLegend ? (
        <table
          role='presentation'
          className={cn(
            'eye:h-fit eye:w-full eye:table-auto eye:self-center eye:text-left',
            legendProps?.className ?? '',
          )}
        >
          <tbody>
            {legendItems.map((item) => (
              <tr
                key={item.key}
                ref={(node) => {
                  legendRowRefs.current[item.index] = node;
                  if (node) {
                    node.tabIndex = node.querySelector(
                      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
                    )
                      ? -1
                      : 0;
                  }
                }}
                onFocus={() => handleHighlight(item.index)}
                onBlur={() => handleHighlight(null)}
                onMouseEnter={() => handleHighlight(item.index)}
                onMouseLeave={() => handleHighlight(null)}
                onKeyDown={(event) => handleLegendKeyDown(event, item.index)}
                className={cn(
                  'eye:cursor-pointer eye:rounded-lg eye:align-middle eye:transition-colors eye:focus-visible:outline-2 eye:focus-visible:outline-offset-2 eye:focus-visible:outline-black',
                  highlightedLegendIndex === item.index
                    ? 'eye:bg-grey-10'
                    : undefined,
                )}
              >
                <td className='eye:py-4 eye:pr-4 eye:pl-8 eye:align-middle'>
                  <span
                    aria-hidden
                    className='eye:block eye:h-16 eye:w-16 eye:rounded-sm'
                    style={{ backgroundColor: item.color }}
                  />
                </td>
                <td className='eye:px-8 eye:py-4 eye:whitespace-nowrap'>
                  {typeof item.value !== 'undefined' ? (
                    <span className='eye:body-l eye:font-semibold'>
                      {formatValue(item.value as ChartPointValue)}
                    </span>
                  ) : null}
                </td>
                <td className='eye:px-8 eye:py-4'>
                  {legendProps?.renderLabel ? (
                    legendProps.renderLabel(item)
                  ) : (
                    <span className='eye:body-m eye:font-medium'>
                      {labelFormatter
                        ? labelFormatter(item.label as ChartPointValue)
                        : item.label}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
